import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userAuthStateListener } from "../../redux/slices/authSlice"; // Make sure the path is correct
import {
  createNavigationContainerRef,
  DefaultTheme as NavigationDefaultTheme,
  NavigationContainer,
} from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import AuthScreen from "../../screens/auth";
import { AppDispatch, RootState } from "../../redux/store";
import HomeScreen from "../home";
import { Platform, View } from "react-native";
import SavePostScreen from "../../screens/savePost";
import EditProfileScreen from "../../screens/profile/edit";
import EditProfileFieldScreen from "../../screens/profile/edit/field";
import Modal from "../../components/modal";
import FeedScreen from "../../screens/feed";
import ProfileScreen from "../../screens/profile";
import ChatSingleScreen from "../../screens/chat/single";
import { ColumnVideo } from "../../screens/columns";
import { beginPageVisit } from "../../services/accessTracking";
import { localizedSiteTitle } from "../../i18n/strings";

export type RootStackParamList = {
  home: undefined;
  auth: undefined;
  userPosts: { creator: string; profile: boolean };
  profileOther: { initialUserId: string };
  savePost: { source: string; sourceThumb: string };
  editProfile: undefined;
  editProfileField: { title: string; field: string; value: string };
  chatSingle: { chatId?: string; contactId?: string };
  columnVideo: { videos: ColumnVideo[]; initialIndex: number };
};

const Stack = createNativeStackNavigator<RootStackParamList>();
const navigationRef = createNavigationContainerRef<RootStackParamList>();
const mobileNavigationTheme = {
  ...NavigationDefaultTheme,
  dark: false,
  colors: {
    ...NavigationDefaultTheme.colors,
    primary: "#28c99a",
    background: "#06110e",
    card: "#06110e",
    text: "#f2f5f4",
    border: "#285848",
    notification: "#d7b65d",
    secondaryContainer: "#159268",
    onSecondaryContainer: "#f2f5f4",
  },
} as typeof NavigationDefaultTheme;

export default function Route() {
  const currentUserObj = useSelector((state: RootState) => state.auth);
  const finishVisitRef = useRef<null | (() => void)>(null);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(userAuthStateListener());
  }, [dispatch]);

  useEffect(() => () => finishVisitRef.current?.(), []);

  const trackCurrentRoute = () => {
    if (!navigationRef.isReady()) return;
    const route = navigationRef.getCurrentRoute();
    finishVisitRef.current?.();
    finishVisitRef.current = beginPageVisit(
      route?.name || "unknown",
      currentUserObj.currentUser,
    );
  };

  if (!currentUserObj.loaded) {
    return <View></View>;
  }

  return (
    <NavigationContainer
      theme={mobileNavigationTheme}
      ref={navigationRef}
      onReady={trackCurrentRoute}
      documentTitle={{
        formatter: () => localizedSiteTitle(),
      }}
      onStateChange={() => {
        if (Platform.OS === "web") {
          document.title = localizedSiteTitle();
        }
        trackCurrentRoute();
      }}
    >
      <Stack.Navigator>
        {currentUserObj.currentUser == null ? (
          <Stack.Screen
            name="auth"
            component={AuthScreen}
            options={{ headerShown: false }}
          />
        ) : (
          <>
            <Stack.Screen
              name="home"
              component={HomeScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="savePost"
              component={SavePostScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="userPosts"
              component={FeedScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="profileOther"
              component={ProfileScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="editProfile"
              component={EditProfileScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="editProfileField"
              component={EditProfileFieldScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="chatSingle"
              component={ChatSingleScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="columnVideo"
              component={FeedScreen}
              options={{ headerShown: false }}
            />
          </>
        )}
      </Stack.Navigator>
      <Modal />
    </NavigationContainer>
  );
}
